"use client";

import { useEffect } from "react";
import type { LegacyAction } from "../components/TouchControls";

type KeyboardMode = "A" | "B";

const mapAction = (event: KeyboardEvent, mode: KeyboardMode): LegacyAction | null => {
  const code = event.code;

  if (mode === "B") {
    if (code === "KeyZ") return "left";
    if (code === "KeyC") return "right";
    if (code === "KeyX") return "softDrop";
    if (code === "KeyN" || code === "Space") return "cw";
    if (code === "KeyB" || code === "ArrowUp") return "ccw";
    return null;
  }

  if (code === "ArrowLeft") return "left";
  if (code === "ArrowRight") return "right";
  if (code === "ArrowDown") return "softDrop";
  if (code === "ArrowUp" || code === "Space") return "cw";
  if (
    code === "IntlRo" ||
    code === "NonConvert" ||
    code === "ShiftLeft" ||
    code === "ShiftRight" ||
    code === "ControlLeft" ||
    code === "ControlRight"
  ) {
    return "ccw";
  }

  return null;
};

export function useLegacyKeyboard(
  enabled: boolean,
  onInput: (action: LegacyAction, held: boolean) => void,
  mode: KeyboardMode = "A",
) {
  useEffect(() => {
    if (!enabled) return;

    const pressedCodes = new Map<string, LegacyAction>();

    const keyDown = (event: KeyboardEvent) => {
      const action = mapAction(event, mode);
      if (!action) return;
      event.preventDefault();
      if (pressedCodes.has(event.code)) return;
      pressedCodes.set(event.code, action);
      onInput(action, true);
    };

    const keyUp = (event: KeyboardEvent) => {
      const action = pressedCodes.get(event.code);
      if (!action) return;
      event.preventDefault();
      pressedCodes.delete(event.code);
      onInput(action, false);
    };

    const releaseAll = () => {
      for (const action of pressedCodes.values()) onInput(action, false);
      pressedCodes.clear();
    };

    window.addEventListener("keydown", keyDown, { passive: false });
    window.addEventListener("keyup", keyUp, { passive: false });
    window.addEventListener("blur", releaseAll);

    return () => {
      releaseAll();
      window.removeEventListener("keydown", keyDown);
      window.removeEventListener("keyup", keyUp);
      window.removeEventListener("blur", releaseAll);
    };
  }, [enabled, mode, onInput]);
}

